/**
 * Copyright (c) 2019-2021 2022 AIGIA All Rights Reserved.
 */

"use strict";

import {IShotView} from "../../../../../types/aig/server";
import {Callback, IErrorObject} from "../../../../../types/platform/universe";

import {Directive, EventEmitter, Input, Output} from "@angular/core";
import {CdkDragDrop, copyArrayItem} from "@angular/cdk/drag-drop";
import {AbstractControl, FormBuilder, FormGroup} from "@angular/forms";

import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {MatSnackBar} from "@angular/material/snack-bar";

import {differenceBy, filter, intersectionBy} from "lodash";

import {SessionService} from "../../../platform/base/services/session.service";
import {ShotdataService} from "../../shotdata/shotdata.service";
import {ShotScoreService} from "../shot-score.service";

import {UpdatableComponent} from "../../../platform/base/components/updatable.component";
import {UpdateListComponent} from "./update-dialog/update-dialog.component";
import {YesNoDialogComponent} from "../../../platform/base/components/yes-no-dialog/yes-no-dialog.component";
import {MessageDialogComponent} from "../../base/components/message-dialog/message-dialog.component";
import {Router} from "@angular/router";
import {TranslateService} from "@ngx-translate/core";

interface IRange {
	from: number;
	to: number;
}

interface IPeriod {
	from: Date;
	to: Date;
}

interface IField {
	create: IPeriod;
	name: string;
	username: string;
	nickname: string;
	score: IRange;
	aria: string;
	gender: string;
	club: number;
	like: string;
}

@Directive()
export class ListBaseComponent extends UpdatableComponent {
	/**
	 * @returns AbstractControl start
	 */
	public get start(): AbstractControl {
		return this.range.get("start");
	}

	/**
	 * @returns AbstractControl count
	 */
	public get _count(): AbstractControl {
		return this.range.get("count");
	}

	@Input() public locked: boolean;
	@Input() public position: any = {x: 0, y: 0};
	@Input() public visible: boolean;
	@Input() public active: boolean;
	@Input() public layout: any;
	@Input() public maxSelected: any;

	@Output() public visibleChange = new EventEmitter<boolean>();
	@Output() public progrssChange = new EventEmitter<boolean>();
	@Output() public dragEnd = new EventEmitter<any>();
	@Output() public mouseDown = new EventEmitter<any>();

	public sorter_slide_position: string = "sorter_slide_start";
	public finder_slide_position: string = "finder_slide_start";
	public title: string = "list";
	public data: IField = {create: {from: new Date(2020, 0), to: new Date()}, name: "", username: "", nickname: "", score: {from: 0, to: 0}, aria: "", gender: "0", club: 0, like: "0"};

	public sortDirection: string = "asc";
	public sortCategory: string = "create";
	public count: number = 0;
	public limit: number = 12; // list window size.
	public current_position: number = 0;
	public query: object = {};
	public option: object = {};
	public range: FormGroup;
	public selected: any[] = [];
	public _shots: any[] = [];

	public clubs:{name: string, value: number}[];

	@Input() public shots: any[] = [];
	@Output() public onChange = new EventEmitter<any>();
	@Output() public onDelete = new EventEmitter<any>();

	constructor(
		protected listservice: ShotdataService,
		protected session: SessionService,
		protected matDialog: MatDialog,
		protected snackbar: MatSnackBar,
		protected formBuilder: FormBuilder,
		protected shotscore: ShotScoreService,
		protected router: Router,
		public translate: TranslateService,
	) {
		super(session, matDialog);
		this.clubs = shotscore.clubs;
		this.service = listservice;
		this.maxSelected = 10;
	}

		/**
	 * リストビューデコレータ
	 *
	 * @param object デコレーション対象
	 */
	public toListView(object: any): any {
		return object;
	}

	/**
	 * ビューデコレータ
	 *
	 * @param data デコレーション対象
	 */
	public toView(data: any): any {
		return data;
	}

	/**
	 * トランスフォーマー
	 * @param data トランスフォーム対象
	 */
	public confirmToModel(data: any): any {
		return data;
	}

	/**
	 *
	 */
	private makeField(object: any, name: string, value: any): any {
		object[name] = value;
		return object;
	}

	/**
	 *
	 */
	private between(field: string, range: IRange): any {
		let result: any;
		const greater: number = this.greater(range);
		const lesser: number = this.lesser(range);

		if (greater === lesser) {
			result = this.makeField({}, field, {$eq: greater});
		} else {
			const from: any = this.makeField({}, field, {$gt: lesser});
			const to: any = this.makeField({}, field, {$lt: greater});
			result = {$and: [from, to]};
		}

		return result;
	}

	/**
	 *
	 */
	private period(field: string, range: IPeriod): any {
		const from: Date = range.from;
		const to: Date = range.to;
		const after: any = this.makeField({}, field, {$gte: from});
		const before: any = this.makeField({}, field, {$lte: to});
		return {$and: [after, before], convert_to: "period"};

	}

	/**
	 *
	 */
	private valueRange(range: IRange): boolean {
		return Boolean(range.from) && Boolean(range.to);
	}

	/**
	 *
	 */
	private valuePeriod(range: IPeriod): boolean {
		return Boolean(range.from) && Boolean(range.to);
	}

	/**
	 * from/toの大きい方
	 */
	private greater(range: IRange): number {
		let result = range.from;
		if (result < range.to) {
			result = range.to;
		}
		return result;
	}

	/**
	 * from/toの小さい方
	 */
	private lesser(range: IRange): number {
		let result = range.from;
		if (result > range.to) {
			result = range.to;
		}
		return result;
	}

	private messageDialog(title: string, message: string, code: string, callback: (result: any) => void = (result:any) => {}): void {
		const dialog: MatDialogRef<any> = this.matDialog.open(MessageDialogComponent, {
			width: "30%",
			minWidth: "320px",
			height: "fit-content",
			data: {content: {title: "", message: message}},
			disableClose: true,
		});

		dialog.afterClosed().subscribe((result: any): void => {
			callback(result);
		});
	}

	/**
	 * エラーダイアログ
	 *
	 * @returns none
	 */
	private errorDialog(error: IErrorObject, callback: (result: any) => void = (error:IErrorObject) => {}): void {
		if (error) {
			if (error.code === 1) {
				this.router.navigate(['/']);
			} else {
				const dialog: MatDialogRef<any> = this.matDialog.open(MessageDialogComponent, {
					width: "30%",
					minWidth: "320px",
					height: "fit-content",
					data: {content: {title: "Error.", message: error.message, tag: error.tag}},
					disableClose: true,
				});

				dialog.afterClosed().subscribe((result: any): void => {
					callback(result);
				});
			}
		}
	}

	private deleteErrorDialog(error: IErrorObject, callback: (result: any) => void = (error:IErrorObject) => {}): void {
		if (error) {
			const dialog: MatDialogRef<any> = this.matDialog.open(MessageDialogComponent, {
				width: "30%",
				minWidth: "320px",
				height: "fit-content",
				data: {content: {title: "Error.", message: error.message.replace(/。/g, "。<br>"), tag: error.tag}},
				disableClose: true,
			});

			dialog.afterClosed().subscribe((result: any): void => {
				callback(result);
				window.location.reload();
			});
		}
	}


	/**
	 *
	 */
	protected buildOption(): void {

		let sort: any = {create: -1};

		let direction = -1;
		if (this.sortDirection === "desc") {
			direction = 1;
		}

		switch (this.sortCategory) {
			case "name":
				sort = {"platform.content.nickname": direction};
				break;
			case "score":
				sort = {"platform.description.score": direction};
				break;
			case "create":
				sort = {"create": direction};
				break;
			case "club":
				sort = {"platform.description.club": direction};
				break;
			default:
		}

		this.option = {sort: sort, skip: this.current_position, limit: this.limit};
	}

	/**
	 *
	 */
	protected buildQuery(fields: IField): void {

		let result: any = {};
		const conditions: any[] = [];

		if (this.valuePeriod(fields.create)) {
			conditions.push(this.period("create", fields.create));
		}

		if (fields.name) {
			conditions.push({name: {$regex: fields.name}});
		}

		if (fields.nickname) {
			conditions.push({"platform.content.nickname": {$regex: fields.nickname}});
		}

		if (fields.username) {
			conditions.push({"platform.username": {$regex: fields.username}});
		}

		if (fields.gender !== "0") {
			conditions.push({"platform.content.gender": fields.gender});
		}

		if (fields.club !== 0) {
			conditions.push({"platform.description.club": fields.club});
		}

		if (fields.like !== "0") {
			const like: boolean = fields.like === "1";
			conditions.push({"platform.description.like": like});
		}

		if (this.valueRange(fields.score)) {
			conditions.push(this.between("platform.description.score", fields.score));
		}

		if (conditions.length > 0) {
			result = {$and: conditions};
		}

		this.query = result;
	}

	/**
	 * undef guard
	 *
	 * @param shot shot
	 *
	 * @returns string nickname
	 */
	public realname(shot: IShotView): string {
		let result: string = "";
		if (shot) {
			result = shot.name;
		}
		return result;
	}

	/**
	 * undef guard
	 *
	 * @param shot shot
	 *
	 * @returns string username
	 */
	public username(shot: IShotView): string {
		let result: string = "";
		if (shot) {
			if (shot.platform) {
				if (shot.platform.username) {
					result = shot.platform.username
				}
			}
		}
		return result;
	}

	/**
	 * undef guard
	 *
	 * @param shot shot
	 *
	 * @returns  nickname
	 */
	public nickname(shot: IShotView): string {
		let result: string = "";
		if (shot) {
			if (shot.platform) {
				if (shot.platform.content) {
					if (shot.platform.content.nickname) {
						result = shot.platform.content.nickname;
					}
				}
			}
		}
		return result;
	}

	/**
	 * undef guard
	 *
	 * @param shot shot
	 *
	 * @returns boolean isown
	 */
	public own(shot: IShotView): boolean {
		let result: boolean = false;
		if (shot) {
			result = true; //  shot.isown; // :todo
		}
		return result;
	}

	/**
	 * undef guard
	 *
	 * @param shot shot
	 *
	 * @returns boolean isPublic
	 */
	public isPublic(shot: IShotView): number {
		let result: number = 0;
		if (shot) {
			if (shot.platform) {
				if (shot.platform.scope) {
					result = shot.platform.scope;
				}
			}
		}
		return result;
	}

	/**
	 * undef guard
	 *
	 * @param shot shot
	 *
	 * @returns message
	 */
	public message(shot: IShotView): string {
		let result: string = "";
		if (shot) {
			if (shot.platform) {
				if (shot.platform.description) {
					if (shot.platform.description.message) {
						result = shot.platform.description.message;
					}
				}
			}
		}
		return result;
	}

	/**
	 * @returns none
	 */
	public onCheck(node: any): void {

		const selected: any[] = [];
		this.selected.forEach((shot) => {
			if (node._id === shot._id) {
				shot.isSelected = !shot.isSelected;
			}
			if (!shot.isSelected) {
				selected.push(shot);
			}
		});
		this.onChange.emit(selected);
	}

	/**
	 * @returns none
	 */
	public onDropSelect(event: CdkDragDrop<string[]>, select: boolean): void {
		this.Progress(true);
		try {
			if (select) {
				this.selected.splice(event.previousIndex, 1);
			} else {
				copyArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
			}
			this.onChange.emit(this.selected);
		} finally {
			this.Progress(false);
		}
	}

	/**
	 * @returns none
	 */
	public onDeselect(item: any): void {
		this.Progress(true);
		try {
			this.selected = filter(this.selected, (_item: any) => {
				return (_item._id !== item._id);
			});
			this._shots = intersectionBy(this._shots, '_id');
			this.onChange.emit(this.selected);
		} finally {
			this.Progress(false);
		}
	}

	/**
	 * @returns none
	 */
	public onSearchValueChange(): void {
		this.Progress(true);
		this.draw((error, result): void => {
			this.Progress(false);
			if (error) {
				this.errorDialog(error);
			}
		});
	}

	/**
	 * @returns none
	 */
	public findDialog(callback: () => void): void {
	}

	/**
	 * @returns boolean selected
	 */
	public isSelected(item: IShotView): boolean {
		const select = differenceBy([item], this.selected, "_id");
		return select.length !== 1;
	}

	/**
	 * @returns none
	 */
	public onSorterSlide(): void {
		if (this.sorter_slide_position === "sorter_slide_start") {
			this.sorter_slide_position = "sorter_slide_end";
		} else {
			this.sorter_slide_position = "sorter_slide_start";
		}
	}

	/**
	 * @returns none
	 */
	public onFinderSlide(): void {
		if (this.finder_slide_position === "finder_slide_start") {
			this.finder_slide_position = "finder_slide_end";
		} else {
			this.finder_slide_position = "finder_slide_start";
		}
	}

	/**
	 * @returns none
	 */
	public draw(callback: Callback<object[]>): void {
		try {
			if (this.current_position >= 0 && (this.limit > 0 && this.limit <= 12)) {
				// sort: {"sm.前傾角度.address.metrics": -1}など。。
				//  	this.option = {sort: {create: -1}, skip: this.current_position, limit: this.limit};
				this.Progress(true);
				this.service.count(this.query, (error: IErrorObject, result: any): void => {
					if (!error) {
						this.count = result.value;
						this._shots.length = 0;
						this.service.query(this.query, this.option, (error: IErrorObject, shots: any[]): void => {
							if (!error) {
								this._shots = shots;
								callback(null, this._shots);
							} else {
								callback(error, null);
							}
							this.Progress(false);
						});
					} else {
						callback(error, null);
					}
				});
			}
		} catch (error: any) {
			callback(error, null);
		}
	}

	/**
	 * デリートダイアログ
	 *
	 * @param event
	 * @param id
	 */
	public onDeleteShot(event: MouseEvent, id: string): void {

		const _delete = (id: string): void => {
			this.Progress(true);
			this.service.delete(id, (): void => {
				this.draw((error, result): void => {
					this.Progress(false);
					if (error) {
						this.errorDialog(error);
					}
				});
			});
		}

		if (event.shiftKey) { // dialog?
			_delete(id);
		} else {
			const resultDialogContent: any = {title: "スイングデータ削除", message: "このスイングデータを削除しますか?", ok_button: "削除"};
			const dialog: MatDialogRef<any> = this.matDialog.open(YesNoDialogComponent, {
				width: "50%",
				minWidth: "320px",
				height: "fit-content",
				data: {
					session: this.currentSession,
					content: resultDialogContent,
				},
				disableClose: true,
			});
			dialog.afterClosed().subscribe((result: object): void => {
				if (result) { // if not cancel then
					_delete(id);
				}
			});
		}
	}

	/**
	 * @returns none
	 */
	public onUpdateShot(event: any): void {
		this.infoUpdateDialog(event, (error, result): void => {
			if (!error) {
				this.Progress(true);
				this.buildQuery(this.data);
				this.draw((error, result) => {
					this.Progress(false);
					if (error) {
						this.errorDialog(error);
					}
				});
			} else {
				this.errorDialog(error);
			}
		});
	}

	/**
	 * @returns none
	 */
	public infoUpdateDialog(shot: any, callback: Callback<any>): void {
		const dialog: MatDialogRef<any> = this.matDialog.open(UpdateListComponent, {
			width: "50%",
			minWidth: "320px",
			height: "fit-content",
			data: shot,
			disableClose: true,
		});

		dialog.beforeClosed().subscribe((result: any): void => {
			if (result) { // if not cancel then
				const id: string = shot._id;
			// 	shot.platform.description.score = this.shotscore.score(shot);

				// TODO: score
				const postureScore = this.shotscore.postureScore(shot);
				const ballisticScore = this.shotscore.ballisticScore(shot);

				shot.platform.description.postureScore = postureScore;
				shot.platform.description.ballisticScore = ballisticScore;
				if (ballisticScore) {
					shot.platform.description.score = (ballisticScore + postureScore) / 2;
				} else {
					shot.platform.description.score =  postureScore;
				}

		// 		shot.platform.description.score = (this.shotscore.ballisticScore(shot) + this.shotscore.postureScore(shot)) / 2;

				this.update(id, this.confirmToModel(shot), (error: IErrorObject, result: any): void => {
					if (!error) {
						callback(null, result);
					} else {
						callback(error, null);
					}
				});
			}
		});

		dialog.afterClosed().subscribe((result: any): void => {
			callback(null, result);
		});

	}

	/**
	 * @returns none
	 */
	public onSort(): void {
		this.Progress(true);
		this.buildOption();
		this.draw((error, result) => {
			this.Progress(false);
			if (error) {
				this.errorDialog(error);
			}
		});
	}

	/**
	 * @returns none
	 */
	public onFind(): void {
		this.Progress(true);
		this.buildQuery(this.data);
		this.draw((error, result) => {
			this.Progress(false);
			if (error) {
				this.errorDialog(error);
			}
		});
	}

	/**
	 * @returns boolean
	 */
	public hasNext(): boolean {
		return (this.current_position + this.limit) < this.count;
	}

	/**
	 * @returns boolean
	 */
	public hasPrev(): boolean {
		return (this.current_position - this.limit) >= 0;
	}

	/**
	 * @returns void
	 */
	public next(): void {
		if (this.hasNext()) {
			this.Progress(true);
			this.current_position += this.limit;
			this.buildOption();
			this.draw((error, result) => {
				this.Progress(false);
				if (error) {
					this.errorDialog(error);
				}
			});
		}
	}

	/**
	 * @returns void
	 */
	public prev(): void {
		if (this.hasPrev()) {
			this.Progress(true);
			this.current_position -= this.limit;
			this.buildOption();
			this.draw((error, result) => {
				this.Progress(false);
				if (error) {
					this.errorDialog(error);
				}
			});
		}
	}

	/**
	 * @returns is active
	 */
	public _active(): boolean {
		return this.active;
	}

	/**
	 * @returns none
	 */
	public _visible(): { display: string } {
		let visibility_value: string = "block";
		if (!this.visible) {
			visibility_value = "none";
		}
		return {display: visibility_value};
	}

	/**
	 * @returns none
	 */
	public _visibleChange(show: boolean): void {
		this.visibleChange.emit(show);
	}

	/**
	 * @returns none
	 */
	public Progress(progress: boolean): void {
		this.progrssChange.emit(progress);
	}

	/**
	 * @returns none
	 */
	public _dragEnd(event: any): void {
		this.position = {x: this.position.x + event.distance.x, y: this.position.y + event.distance.y};
		this.dragEnd.emit(this.position);
	}

	/**
	 * @returns none
	 */
	public _mouseDown(event: any): void {
		this.mouseDown.emit(event);
	}

	/**
	 *
	 */
	public score(shot: IShotView): number {
		let result: number = 0;
		if (shot.platform) {
			if (shot.platform.description) {
				if (shot.platform.description.score) {
					result = Math.floor(shot.platform.description.score);
				}
			}
		}
		return result;
	}

	/**
	 *
	 */
	public getLike(shot: IShotView): boolean {
		let result: boolean = false;
		if (shot.platform) {
			if (shot.platform.description) {
				if (shot.platform.description.like) {
					result = shot.platform.description.like;
				}
			}
		}
		return result;
	}

	public setLike(shot: IShotView): void {
		const id: string = shot._id;
		const originalLikeState = shot.platform.description.like;
		shot.platform.description.like = !shot.platform.description.like;

		this.update(id, this.confirmToModel(shot), (error: IErrorObject, result: any): void => {
			if (!error) {
				// 	this.draw((error, result) => {
				// 		if (error) {
				// 			this.errorDialog(error);
				// 		}
				// 	});
			} else {
				shot.platform.description.like = originalLikeState;
				this.deleteErrorDialog(error);

			}
		});
	}

	/*
		public listSort(): void {
			switch (this.sortCategory) {
				case "name":
					this.selected = orderBy(this.selected, ['owner.content.nickname'], this.sortDirection);
					break;
				case "score":
					this.selected = orderBy(this.selected, ['platform.description.score'], this.sortDirection);
					break;
				case "create":
					this.selected = orderBy(this.selected, ['create'], this.sortDirection);
					break;
				case "club":
					this.selected = orderBy(this.selected, [' platform.description.club'], this.sortDirection);
					break;
				default:
			}
			this.onChange.emit(this.selected);
		}
	*/
	public scoreStyle(shot: IShotView): any {
		let result: any = {color: "rgb(86,83,105)"};
		if (shot.platform) {
			if (shot.platform.description) {
				if (shot.platform.description.message !== "") {
					result = {color: "rgb(246,2,2)"};
				}
			}
		}
		return result;
	}

	public hasPrizm(shot: any): boolean {
		return Boolean(shot.prizm);
	}

	public clubNumber(shot: IShotView): number {
		let result: number = 0;
		if (shot) {
			if (shot.platform) {
				if (shot.platform.description) {
					if (shot.platform.description.club) {
						result = shot.platform.description.club;
					}
				}
			}
		}
		return result;
	}

	public clubName(shot: IShotView): string {
		let result: string = "";
		if (shot) {
			if (shot.platform) {
				if (shot.platform.description) {
					if (shot.platform.description.club) {
						const No = shot.platform.description.club;
						const club: { name: string, value: number }[] = this.clubs.filter(club => club.value === No);
						if (club.length) {
							result = club[0].name;
						}
					}
				}
			}
		}
		return result;
	}

	public clubColor(value: number): string {
		const hue = 360 - ((360 / 16) * value);
		const luminance = 80;
		const saturation = 70;
		return "hsl(" + hue + ", " + saturation + "%, " + luminance + "%)";
	}

	public clubBorderStyle(shot: IShotView): any {
		let result = {"border-radius": "50%", border: "solid 4px #808080"};
		if (shot) {
			if (shot.platform) {
				if (shot.platform.description) {
					if (shot.platform.description.club) {
						result = {"border-radius": "50%", border: "solid 4px " + this.clubColor(shot.platform.description.club)};
					}
				}
			}
		}
		return result;
	}

	public clubFontStyle(shot: IShotView): any {
		let result = {
			color: "#808080",
			width: "20px",
			height: "20px",
			"font-weight": "900",
			"font-size": "20px",
			"font-family": "'Inconsolata', monospace",
			"letter-spacing": "-2px"
		};
		if (shot) {
			if (shot.platform) {
				if (shot.platform.description) {
					if (shot.platform.description.club) {
						result = {
							color: this.clubColor(shot.platform.description.club),
							width: "20px",
							height: "20px",
							"font-weight": "900",
							"font-size": "20px",
							"font-family": "'Inconsolata', monospace",
							"letter-spacing": "-2px"
						};
					}
				}
			}
		}
		return result;
	}

}
